/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, List, Switch, Typography } from '@mui/material';
import React from 'react';
import { CustomAccordion } from '@opengeoweb/shared';
import { DataExplorerFilterOption } from './DataExplorerFilterOption';
import type { Queryable } from '../../store/types';
import { useDataExplorerTranslation } from '../../utils/i18n';

interface DataExplorerFiltersProps {
  filters: Queryable[];
  meteoGateOnly: boolean;
  onSetFilterValue: (filter: string, value: string, checked: boolean) => void;
  onToggleMeteoGateOnly: (enabled: boolean) => void;
}

export const DataExplorerFilters: React.FC<DataExplorerFiltersProps> = ({
  filters,
  meteoGateOnly,
  onSetFilterValue,
  onToggleMeteoGateOnly,
}: DataExplorerFiltersProps) => {
  const { t } = useDataExplorerTranslation();

  const toggleFilter = (
    checked: boolean,
    filter: string,
    value: string,
  ): void => {
    onSetFilterValue(filter, value, checked);
  };

  const toggleMeteoGateOnly = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    onToggleMeteoGateOnly(event.target.checked);
  };

  return (
    <Box>
      <Box sx={{ marginLeft: '12px' }}>
        <Switch
          checked={meteoGateOnly}
          onChange={toggleMeteoGateOnly}
          size="small"
        />
        <Typography variant="caption">
          {t('dataexplorer-meteogate-only')}
        </Typography>
      </Box>
      <Typography
        color="textSecondary"
        sx={{ marginLeft: '24px' }}
        variant="caption"
      >
        {t('dataexplorer-filters')}
      </Typography>
      {filters?.map(
        (filter) =>
          filter.values.length > 0 && (
            <CustomAccordion
              data-testid={`dataExplorerFilter-${filter.id}`}
              defaultExpanded={false}
              detailsSx={{ padding: '0px !important' }}
              key={filter.id}
              title={
                filter.selected.length > 0
                  ? `${t(filter.label)} (${filter.selected.length})`
                  : t(filter.label)
              }
            >
              <List sx={{ padding: '0px' }}>
                {filter.values.map((value) => (
                  <DataExplorerFilterOption
                    checked={filter.selected.includes(value.value)}
                    count={value.datasetCount}
                    filterId={filter.id}
                    key={`${filter.id}-${value.value}`}
                    onClick={(checked) =>
                      toggleFilter(checked, filter.id, value.value)
                    }
                    optionId={`${filter.id}-${value.value}`}
                    value={value.value}
                  />
                ))}
              </List>
            </CustomAccordion>
          ),
      )}
    </Box>
  );
};
